import {
  ChangeDetectionStrategy,
  Component,
  inject, signal,
  ViewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Checkbox } from 'primeng/checkbox';
import { DatePicker } from 'primeng/datepicker';
import { InputText } from 'primeng/inputtext';
import { Paginator } from 'primeng/paginator';
import { Select } from 'primeng/select';
import { Table, TableModule } from 'primeng/table';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import { AdminUserSearchCriteria } from '../../../shared/models/admin-user-search-criteria';
import { AdminUserSearchResult } from '../../../shared/models/admin-user-search-result';
import { PreviewDataType } from '../../../shared/enums/preview-data-type.enum';
import { SortDirection } from '../../../shared/enums/sort-direction.enum';
import { UserSearchReason } from '../../../shared/enums/user-search-reason.enum';
import { TableResponse } from '../../../shared/models/table-response';
import { TableRequestData } from '../../../shared/models/table/table-request-param';
import { AdminUserApi } from '../../../shared/services/api/admin-user.api';
import { HttpUtils } from '../../../shared/utils/http.utils';
import { TableBaseAbstractComponent } from '../../../shared/components/table-base-abstract.component';
import { ControlErrorDirective } from '../../../shared/directive/control-error.directive';
import { InfoMessageComponent } from '../../../shared/components/info-message/info-message.component';
import { PreviewDataWrapperComponent } from '../../../shared/components/preview-data-wrapper/preview-data-wrapper.component';
import { AdminPanelUsersFormBuilder } from './form-group/admin-panel-users-form.builder';
import { Tooltip } from 'primeng/tooltip';
import { APP_PATHS } from '../../../core/config/app-paths';
import { AdminPanelUserPreviewStateService } from './state/admin-panel-user-preview-state.service';
import { AdminPanelUsersNavigationContextService } from '../../../shared/services/admin-panel-users-navigation-context.service';
import { PrimeFilterTarget } from '../../../shared/models/custom-types';
import { RequiredLabelDirective } from '../../../shared/directive/required-label.directive';

@Component({
  selector: 'app-admin-panel-users',
  imports: [
    Button,
    Card,
    Checkbox,
    ControlErrorDirective,
    DatePicker,
    DatePipe,
    FormsModule,
    InfoMessageComponent,
    InputText,
    Paginator,
    PreviewDataWrapperComponent,
    ReactiveFormsModule,
    Select,
    TableModule,
    Tooltip,
    RequiredLabelDirective,
  ],
  templateUrl: './admin-panel-users.html',
  styleUrl: './admin-panel-users.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AdminUserApi],
})
export class AdminPanelUsers extends TableBaseAbstractComponent<AdminUserSearchResult> {
  @ViewChild('table') override tableRef!: Table;

  readonly #adminUserApi = inject(AdminUserApi);
  readonly #router = inject(Router);
  readonly #userPreviewState = inject(AdminPanelUserPreviewStateService);
  readonly #navigationContext = inject(AdminPanelUsersNavigationContextService);


  protected readonly PreviewDataType = PreviewDataType;
  protected readonly today = new Date();
  protected readonly reasonOptions = [
    {
      label: UserSearchReason.HD_VERIFICATION,
      value: UserSearchReason.HD_VERIFICATION,
    },
    {
      label: UserSearchReason.OTHER,
      value: UserSearchReason.OTHER,
    },
    {
      label: UserSearchReason.OFFICE_REQUEST_VERIFICATION,
      value: UserSearchReason.OFFICE_REQUEST_VERIFICATION,
    },
    {
      label: UserSearchReason.USER_REQUEST,
      value: UserSearchReason.USER_REQUEST,
    },
    {
      label: UserSearchReason.ANNOUNCEMENT_SEND,
      value: UserSearchReason.ANNOUNCEMENT_SEND,
    },
  ];

  protected readonly hasSearched = signal(false);
  protected readonly searchCriteria = signal<AdminUserSearchCriteria | null>(
    null,
  );
  protected tableFilters = {
    lastLoginAt: null as Date | null,
  };
  protected readonly searchForm = AdminPanelUsersFormBuilder.createFormGroup(
    () => !!this.tableFilters.lastLoginAt,
  );

  protected override apiCall$ = (
    params: TableRequestData,
  ): Observable<TableResponse<AdminUserSearchResult>> => {
    const normalizedParams = {
      ...params,
      sortBy: params.sortBy ?? 'lastName',
      order: params.order ?? SortDirection.ASC,
    };

    if (!this.hasSearched()) {
      return of({
        content: [],
        page: {
          size: normalizedParams.pageSize ?? 10,
          number: normalizedParams.pageIndex ?? 0,
          totalElements: 0,
          totalPages: 0,
        },
      });
    }

    return this.#adminUserApi.searchUsers$(
      this.searchCriteria() ?? {},
      normalizedParams,
    );
  };

  constructor() {
    super();
    this.#navigationContext.clear();
    this.tableRequest.update((value) => ({
      ...value,
      pageSize: 5,
    }));
  }

  protected onSearch(): void {
    this.searchForm.updateValueAndValidity();

    if (this.searchForm.invalid) {
      this.searchForm.markAllAsTouched();
      return;
    }

    this.hasSearched.set(true);
    this.searchCriteria.set(this.#buildSearchCriteria());
    this.tableRequest.update((value) => ({
      ...value,
      pageIndex: 0,
    }));
    this.reloadData();
  }

  protected onClearFilters(): void {
    this.searchForm.reset();
    this.#setUserFiltersDisabled(false);
    this.tableFilters = {
      lastLoginAt: null,
    };
    this.searchForm.updateValueAndValidity();
    this.hasSearched.set(false);
    this.searchCriteria.set(null);
    this.resetFilters();
    this.rows.set([]);
    this.tablePage.set(undefined);
  }

  protected onSystemAdministratorChange(isSystemAdministrator: boolean): void {
    if (isSystemAdministrator) {
      this.searchForm.patchValue({
        pesel: null,
        firstName: null,
        lastName: null,
        documentNumber: null,
        birthDate: null,
        email: null,
        city: null,
        reason: null,
      });
    } else {
      this.#setUserFiltersDisabled(false, false);
      this.searchForm.markAsPristine();
      this.searchForm.markAsUntouched();
      this.searchForm.markAsTouched();
      this.searchForm.updateValueAndValidity();
      return;
    }

    this.#setUserFiltersDisabled(true);
    this.searchForm.updateValueAndValidity();
  }

  override onPrimeFilterChange(target: PrimeFilterTarget, nazwa: string): void {
    super.onPrimeFilterChange(target, nazwa);
    this.searchForm.updateValueAndValidity();
  }

  protected showDetails(user: AdminUserSearchResult): void {
    this.#userPreviewState.setUser(user);
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.users}/${APP_PATHS.admin.details}`,
    ]);
  }

  #buildSearchCriteria(): AdminUserSearchCriteria {
    const formValue = this.searchForm.getRawValue();

    return {
      systemAdministrator: formValue.systemAdministrator,
      pesel: formValue.pesel?.trim() || null,
      firstName: formValue.firstName?.trim() || null,
      lastName: formValue.lastName?.trim() || null,
      documentNumber: formValue.documentNumber?.trim() || null,
      birthDate: formValue.birthDate
        ? HttpUtils.formatDateToLocalDateString(formValue.birthDate)
        : null,
      lastLoginAt: this.tableFilters.lastLoginAt
        ? HttpUtils.formatDateToLocalDateString(this.tableFilters.lastLoginAt)
        : null,
      email: formValue.email?.trim() || null,
      city: formValue.city?.trim() || null,
      reason: formValue.reason ?? null,
    };
  }

  #setUserFiltersDisabled(disabled: boolean, emitEvent = true): void {
    const controls = [
      this.searchForm.controls.pesel,
      this.searchForm.controls.firstName,
      this.searchForm.controls.lastName,
      this.searchForm.controls.documentNumber,
      this.searchForm.controls.birthDate,
      this.searchForm.controls.email,
      this.searchForm.controls.city,
      this.searchForm.controls.reason,
    ];

    controls.forEach((control) => {
      if (disabled) {
        control.disable({ emitEvent });
      } else {
        control.enable({ emitEvent });
      }
    });
  }
}
